import {
  createContext,
  useContext,
  useState,
  useMemo,
  useEffect,
  type ReactNode,
  useCallback,
} from 'react';
import { GameState, Song } from '../../types';
import { Category } from '../../data/categories';
import { generateBoard, restoreUsedAnswers } from '../../game';
import { getStoreValues, setStoreValues } from '../../localStore';
import { jsConfetti } from '../../utils/confetti';
import { sendAnalyticsEvent } from '../../utils';
import { GUESS_COUNT } from '../../constants';

type Answers = { [questionId: string]: Song };

interface GameContextType {
  answers: Answers;
  yAxisQuestions: Category[];
  xAxisQuestions: Category[];
  guessCount: number;
  handleCorrectAnswer(questionId: string, answer: Song): void;
  handleWrongAnswer(): void;
  handleContinue(): void;
  usedAnswers: Song[];
  gameState: GameState;
  changeDate(value: number): void;
  customDate: Date;
}

const GameContext = createContext<GameContextType | undefined>(undefined);

export const useGame = () => {
  const context = useContext(GameContext);
  if (!context) {
    throw new Error('useGame must be used within a GameProvider');
  }
  return context;
};

interface GameProviderProps {
  children: ReactNode;
}

export function GameProvider({ children }: GameProviderProps) {
  const [answers, setAnswers] = useState<Answers>({});
  const [guessCount, setGuessCount] = useState(GUESS_COUNT);
  const [yAxisQuestions, setYAxisQuestions] = useState<Category[]>([]);
  const [xAxisQuestions, setXAxisQuestions] = useState<Category[]>([]);
  const [usedAnswers, setUsedAnswers] = useState<Song[]>([]);
  const [currentGameId, setCurrentGameId] = useState(0);
  const [gameState, setGameState] = useState<GameState>('playing');
  const [customDate, setCustomDate] = useState(new Date(Date.now()));

  const changeDate = useCallback(
    (value: number) => {
      const newDate = new Date(customDate);
      newDate.setDate(newDate.getDate() + value);
      setCustomDate(newDate);
    },
    [customDate]
  );

  useEffect(() => {
    const { cols, rows, gameId } = generateBoard(customDate);

    setCurrentGameId(gameId);
    const {
      answers: storeAnswers,
      guessCount: storeGuessCount,
      gameState: storeGameState,
    } = getStoreValues(gameId);

    if (storeGuessCount !== undefined) {
      setGuessCount(storeGuessCount);
    }

    if (storeAnswers) {
      setAnswers(storeAnswers);
      setUsedAnswers((prev) => restoreUsedAnswers(prev, storeAnswers));
    }

    if (storeGameState) {
      setGameState(storeGameState);
    }

    setYAxisQuestions(rows);
    setXAxisQuestions(cols);
  }, [customDate]);

  useEffect(() => {
    if (gameState === 'continue') return;

    if (Object.values(answers).length === 9) {
      setGameState('won');
      setStoreValues(currentGameId, 'gameState', 'won');

      sendAnalyticsEvent('winner', { remainingLives: `${guessCount}` });

      jsConfetti.addConfetti({
        confettiColors: ['#BC5E92', '#CCC964', '#3BADBF', '#B42820'],
      });

      return;
    }

    if (guessCount <= 0) {
      setGameState('lost');
      setStoreValues(currentGameId, 'gameState', 'lost');

      sendAnalyticsEvent('loser');
    }
  }, [answers, guessCount, gameState, currentGameId]);

  const handleWrongAnswer = useCallback(() => {
    const newCount = Math.max(0, guessCount - 1);

    setGuessCount(newCount);
    setStoreValues(currentGameId, 'guessCount', newCount);
  }, [currentGameId, guessCount]);

  const handleCorrectAnswer = useCallback(
    (questionId: string, answer: Song) => {
      const latestAnswers = { ...answers, [questionId]: answer };

      setAnswers(latestAnswers);
      setUsedAnswers((prev) => [...prev, answer]);
      setStoreValues(currentGameId, 'answers', latestAnswers);
    },
    [currentGameId, setAnswers, answers]
  );

  const handleContinue = useCallback(() => {
    setGameState('continue');
    setStoreValues(currentGameId, 'gameState', 'continue');

    sendAnalyticsEvent('continue_playing');
  }, [currentGameId]);

  const value = useMemo(
    () => ({
      answers,
      yAxisQuestions,
      xAxisQuestions,
      guessCount,
      handleCorrectAnswer,
      handleWrongAnswer,
      handleContinue,
      usedAnswers,
      gameState,
      changeDate,
      customDate,
    }),
    [
      answers,
      xAxisQuestions,
      yAxisQuestions,
      guessCount,
      handleCorrectAnswer,
      handleWrongAnswer,
      usedAnswers,
      gameState,
      handleContinue,
      customDate,
      changeDate,
    ]
  );

  return <GameContext.Provider value={value}>{children}</GameContext.Provider>;
}
